import { useState } from "react";

import { StatColumn, StatProps, ToggleChevron, Column, Row, Text } from "@bridgesplit/ui";
import { BsMetaUtil, formatLeverage, getLoopBorrowCapDetails, usePresetMarketInfos } from "@bridgesplit/abf-react";
import { Collapse } from "@mui/material";
import { combineCollections, formatPercent, formatUsd } from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { useOracleStats } from "app/components/oracles";

import { useLoopContext } from "../LoopContext";
import { BorrowCapWarning } from "../../common";

export default function LoopDetailStats() {
    const { loopExpanded } = useLoopContext();
    const borrowCapWarning = getLoopBorrowCapDetails(loopExpanded);

    const [expanded, setExpanded] = useState(false);

    const { getMarketByCollateralAndPrincipalMint, getPrincipalMarketInfo } = usePresetMarketInfos({
        filter: {
            principalMints: loopExpanded?.loopVault.principalMint ? [loopExpanded?.loopVault.principalMint] : undefined
        },
        skip: !loopExpanded?.loopVault.principalMint || !loopExpanded?.loopVault.collateralMint
    });

    const principalAssetMarketInfo = getPrincipalMarketInfo(loopExpanded?.loopVault.principalMint);

    const collateralAssetMarketInfo = getMarketByCollateralAndPrincipalMint(
        loopExpanded?.loopVault.collateralMint,
        loopExpanded?.loopVault.principalMint
    );

    const oracleStats = useOracleStats({
        collateralMarketData: collateralAssetMarketInfo?.ltvInfo,
        principalMarketData: principalAssetMarketInfo?.ltvInfo
    });

    const allStats = combineCollections([oracleStats?.topLevelOracleStats, oracleStats?.customOracleStats]);

    const stats: StatProps[] = [
        {
            value: formatPercent(loopExpanded?.loopVault.collateralApyPct),
            caption: `Base ${BsMetaUtil.getSymbol(loopExpanded?.collateralToken)} APY`
        },
        {
            value: formatPercent(loopExpanded?.loopVault.maxLeveragedApyPct),
            caption: loopExpanded
                ? `${formatLeverage(loopExpanded?.loopVault.maxLeverage)} ${COPY.LEVERAGE_TERM.toLowerCase()} APY`
                : "Max APY"
        },
        {
            value: formatUsd(loopExpanded?.collateralOracle.getUsdAmount(loopExpanded.loopVault.collateralDeposited)),
            caption: COPY.LOOP_COLLATERAL_DEPOSIT_TERM
        },
        {
            value: (
                <BorrowCapWarning borrowCapWarning={borrowCapWarning?.warningMessage}>
                    {formatUsd(
                        loopExpanded?.principalOracle.getUsdAmount(
                            Math.min(
                                loopExpanded?.loopVault?.principalAmountAvailable,
                                borrowCapWarning?.remainingGlobalCapacity ?? 0
                            )
                        )
                    )}
                </BorrowCapWarning>
            ),
            caption: COPY.LOOP_PRINCIPAL_AVAILABLE_TERM
        },
        {
            caption: (
                <Row spacing={0.5}>
                    <Text color="caption">{COPY.ORACLE_TERM_PLURAL}</Text>
                    <ToggleChevron
                        sx={{ p: 0 }}
                        onClick={() => setExpanded((prev) => !prev)}
                        textVariant="body1"
                        expanded={expanded}
                    />
                </Row>
            ),
            value: ""
        }
    ];

    return (
        <Column spacing={0.5} sx={{ width: "100%" }}>
            <StatColumn variant="body1" stats={stats} loading={!loopExpanded} />
            <Collapse in={expanded}>
                <StatColumn variant="body1" stats={allStats} />
            </Collapse>
        </Column>
    );
}
